import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Box,
  Typography,
} from '@mui/material';
import { PreparationAreaVm } from 'api/production/preparation-area.vm';
import { GetPreparationAreasVm } from 'api/production/get-preparation-areas.vm';
import { getPreparationAreas } from 'api/production/get-preparation-areas';
import { useSnackbar } from 'hooks/use-snackbar';

interface PreparationAreaSelectionDialogProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (preparationAreaId: string) => void;
  loading?: boolean;
  warehouseId: string;
}

const PreparationAreaSelectionDialog: React.FC<PreparationAreaSelectionDialogProps> = ({
  open,
  onClose,
  onSubmit,
  loading = false,
  warehouseId,
}) => {
  const [selectedAreaId, setSelectedAreaId] = useState('');
  const [areas, setAreas] = useState<PreparationAreaVm[]>([]);
  const [areasLoading, setAreasLoading] = useState(false);
  const snackbar = useSnackbar();

  const fetchPreparationAreas = () => {
    setAreasLoading(true);
    getPreparationAreas(warehouseId, '', {
      success: (response: GetPreparationAreasVm) => {
        setAreas(response.objects);
        setAreasLoading(false);
      },
      error: (err) => {
        console.error('Falha ao obter áreas de preparação:', err);
        snackbar.show('Falha ao carregar áreas de preparação', null, 'error');
        setAreasLoading(false);
      },
    });
  };

  useEffect(() => {
    if (open) {
      fetchPreparationAreas();
      setSelectedAreaId('');
    }
  }, [open, warehouseId]);

  const handleSubmit = () => {
    if (selectedAreaId) {
      onSubmit(selectedAreaId);
    }
  };

  const handleClose = () => {
    setSelectedAreaId('');
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Selecionar Área de Preparação</DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
          {areasLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
              <CircularProgress />
            </Box>
          ) : areas.length === 0 ? (
            <Typography color="text.secondary" align="center">
              Nenhuma área de preparação disponível neste armazém
            </Typography>
          ) : (
            <FormControl fullWidth required>
              <InputLabel>Área de Preparação</InputLabel>
              <Select
                value={selectedAreaId}
                label="Área de Preparação"
                onChange={(e) => setSelectedAreaId(e.target.value)}
                disabled={loading}
              >
                {areas.map((area) => (
                  <MenuItem key={area.id} value={area.id}>
                    {area.id}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={loading}>
          Cancelar
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={!selectedAreaId || loading || areas.length === 0}
          startIcon={loading ? <CircularProgress size={20} /> : null}
        >
          {loading ? 'A atribuir...' : 'Atribuir'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PreparationAreaSelectionDialog;
